import React, { useState, useCallback, useEffect } from 'react'
import { Box, TextField, Typography, Button, Tooltip, IconButton } from '@mui/material'
import { FIELD_TYPES } from '../../../utility/constants'
import { toast } from 'react-toastify'
import adminFieldService from '../../../services/admin/field'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { Delete } from '@mui/icons-material'

const getSchemaType = (dataStructure) => {
  if ([FIELD_TYPES.TEXT, FIELD_TYPES.BIGTEXT].includes(dataStructure)) return 'text'
  if ([FIELD_TYPES.NUMBER, FIELD_TYPES.CURRENCY].includes(dataStructure)) return 'numeric'
  return null
}

const isValidRegex = (pattern) => {
  if (!pattern) return true
  try {
    new RegExp(pattern)
    return true
  } catch {
    return false
  }
}

const TEXT_KEYS = ['minLength', 'maxLength', 'regExPattern', 'errorMessage']

const NUMERIC_KEYS = ['min', 'max', 'errorMessage']

const stripIrrelevantKeys = (rules = {}, schemaType) => {
  const keepKeys = schemaType === 'text' ? TEXT_KEYS : schemaType === 'numeric' ? NUMERIC_KEYS : []
  return Object.fromEntries(Object.entries(rules).filter(([k]) => keepKeys.includes(k)))
}

function TextValidationRules({ rules, onChange }) {
  const [regexError, setRegexError] = useState('')

  const update = (patch) => onChange({ ...rules, ...patch })

  const handleRegexChange = (value) => {
    setRegexError(value && !isValidRegex(value) ? 'Invalid regular expression.' : '')
    update({ regExPattern: value || null })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
      <TextField
        type="number"
        size="small"
        fullWidth
        variant="outlined"
        color="primary"
        label="Enter minimum length"
        value={rules?.minLength ?? ''}
        inputProps={{ min: 0 }}
        onChange={(e) =>
          update({ minLength: e.target.value !== '' ? parseInt(e.target.value, 10) : null })
        }
      />
      <TextField
        type="number"
        size="small"
        fullWidth
        variant="outlined"
        color="primary"
        label="Enter maximum length"
        value={rules?.maxLength ?? ''}
        inputProps={{ min: 0 }}
        onChange={(e) =>
          update({ maxLength: e.target.value !== '' ? parseInt(e.target.value, 10) : null })
        }
      />
      <TextField
        size="small"
        fullWidth
        variant="outlined"
        color="primary"
        label="Enter regex pattern"
        value={rules?.regExPattern ?? ''}
        onChange={(e) => handleRegexChange(e.target.value)}
        placeholder="^[A-Z]{5}[0-9]{4}[A-Z]{1}$"
        error={!!regexError}
      />
      <TextField
        size="small"
        fullWidth
        variant="outlined"
        color="primary"
        label="Error Message"
        value={rules?.errorMessage ?? ''}
        onChange={(e) => update({ errorMessage: e.target.value || null })}
        placeholder="Incorrect value."
      />
    </Box>
  )
}

function NumericValidationRules({ rules, onChange }) {
  const update = (patch) => onChange({ ...rules, ...patch })

  const minVal = rules?.min ?? ''
  const maxVal = rules?.max ?? ''

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
      <TextField
        type="number"
        size="small"
        fullWidth
        variant="outlined"
        color="primary"
        label="Min Value"
        value={minVal}
        onChange={(e) => update({ min: e.target.value !== '' ? parseFloat(e.target.value) : null })}
      />
      <TextField
        type="number"
        size="small"
        fullWidth
        variant="outlined"
        color="primary"
        label="Max Value"
        value={maxVal}
        onChange={(e) => update({ max: e.target.value !== '' ? parseFloat(e.target.value) : null })}
      />
      <TextField
        size="small"
        fullWidth
        variant="outlined"
        color="primary"
        label="Error Message"
        value={rules?.errorMessage ?? ''}
        onChange={(e) => update({ errorMessage: e.target.value || null })}
        placeholder="Incorrect value."
      />
    </Box>
  )
}

export default function ValidationRulesUI({ fieldTemplate, setFieldTemplate, onSave }) {
  const { user } = useAuthContext()
  const schemaType = getSchemaType(fieldTemplate?.data_structure)

  const hasExistingRules =
    fieldTemplate?.validation_rules && Object.keys(fieldTemplate.validation_rules).length > 0

  const [expanded, setExpanded] = useState(false)
  const [localRules, setLocalRules] = useState(fieldTemplate?.validation_rules || {})
  const [isRemoving, setIsRemoving] = useState(false)

  const handleExpand = () => {
    const existing = fieldTemplate?.validation_rules || {}
    const schemaType = getSchemaType(fieldTemplate?.data_structure)
    const cleanedRules = stripIrrelevantKeys(existing, schemaType)
    setLocalRules(cleanedRules)
    setExpanded(true)
  }

  useEffect(() => {
    const existing = fieldTemplate?.validation_rules || {}
    const cleaned = stripIrrelevantKeys(existing, schemaType)
    setLocalRules(cleaned)
  }, [schemaType, fieldTemplate?.id])

  const handleChange = useCallback(
    (updatedRules) => {
      const relevant = stripIrrelevantKeys(updatedRules, schemaType)
      const cleaned = Object.fromEntries(
        Object.entries(relevant).filter(([, v]) => v !== null && v !== undefined && v !== ''),
      )
      setLocalRules(Object.keys(cleaned).length > 0 ? cleaned : {})
    },
    [schemaType],
  )

  const hasInvalidRegex = (() => {
    const pattern = localRules?.regExPattern
    if (!pattern) return false
    try {
      new RegExp(pattern)
      return false
    } catch {
      return true
    }
  })()

  const handleSave = () => {
    if (schemaType === 'text') {
      if (!localRules?.minLength) {
        toast.error('Minimum length is required')
        return
      }
      if (!localRules?.maxLength) {
        toast.error('Maximum length is required')
        return
      }
      if (!localRules?.regExPattern) {
        toast.error('Regex pattern is required')
        return
      }
      if (hasInvalidRegex) {
        toast.error('Invalid regex pattern')
        return
      }
      if (!localRules?.errorMessage) {
        toast.error('Error message is required')
        return
      }
    }

    if (schemaType === 'numeric') {
      if (localRules?.min === undefined || localRules?.min === null) {
        toast.error('Minimum value is required')
        return
      }
      if (localRules?.max === undefined || localRules?.max === null) {
        toast.error('Maximum value is required')
        return
      }
      if (Number(localRules.min) > Number(localRules.max)) {
        toast.error('Min cannot be greater than Max')
        return
      }
      if (!localRules?.errorMessage) {
        toast.error('Error message is required')
        return
      }
    }

    const finalRules = Object.keys(localRules).length > 0 ? localRules : null

    const updatedTemplate = {
      ...fieldTemplate,
      validation_rules: finalRules,
    }

    setFieldTemplate(updatedTemplate)

    if (typeof onSave === 'function') onSave(updatedTemplate)

    toast.success('Validation rules saved successfully')

    setExpanded(false)
  }

  const handleCancel = () => {
    setLocalRules(fieldTemplate?.validation_rules || {})
    setExpanded(false)
  }

  const handleRemove = async () => {
    try {
      setIsRemoving(true)

      await adminFieldService.removeValidationRules(user, fieldTemplate?.id)

      const updatedTemplate = {
        ...fieldTemplate,
        validation_rules: null,
      }

      setLocalRules({})
      setFieldTemplate(updatedTemplate)

      toast.success('Validation rules successfully removed')

      setExpanded(false)
    } catch (err) {
      console.error('Failed to remove validation rules:', err)
      toast.error('Failed to remove validation rules. Please try again.')
    } finally {
      setIsRemoving(false)
    }
  }

  if (!schemaType) return null

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 400 }}>
          Validation Rules
        </Typography>
        {!expanded && (
          <Button size="small" variant="contained" onClick={handleExpand}>
            {hasExistingRules ? 'Edit' : 'Add'}
          </Button>
        )}
      </Box>

      {expanded && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
          {schemaType === 'text' ? (
            <TextValidationRules rules={localRules} onChange={handleChange} />
          ) : (
            <NumericValidationRules rules={localRules} onChange={handleChange} />
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 0.5 }}>
            {hasExistingRules && (
              <Tooltip title="Remove Validation Rules">
                <IconButton
                  onClick={handleRemove}
                  disabled={isRemoving}
                  sx={{
                    color: '#708090',
                    '&:hover': {
                      backgroundColor: 'rgba(112, 128, 144, 0.08)',
                    },
                  }}
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
            <Button size="small" variant="outlined" onClick={handleCancel} disabled={isRemoving}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isRemoving}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}
